import {I18nKeys} from '@multipass-i18n';
import {Check, FileText, Mail, User} from 'lucide-react';
import * as React from 'react';

import {useT} from '../../logic';

interface BrandPointProps {
    bold: string;
    text: string;
}

const BrandPoint: React.FunctionComponent<BrandPointProps> = ({bold, text}: BrandPointProps) => (
    <li className="flex items-start gap-3 text-body">
        <span className="mt-px grid h-[26px] w-[26px] flex-none place-items-center rounded-full bg-text-on-color/15">
            <Check size={16}/>
        </span>
        <span className="opacity-85">
            <b className="font-semibold">{bold}</b> {text}
        </span>
    </li>
);

export const AuthLayout: React.FunctionComponent<React.PropsWithChildren> = ({children}: React.PropsWithChildren) => {
    const t = useT();
    return (
        <div className="grid min-h-[calc(100vh-61px)] lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]">
            <section className="relative hidden overflow-hidden bg-brand-emphasis px-12 py-16 text-text-on-color lg:flex lg:flex-col lg:justify-between lg:gap-12" aria-hidden="true">
                <span className="pointer-events-none absolute -bottom-[12%] -right-[6%] select-none font-hanzi-reading text-[540px] leading-none opacity-[0.07]">學</span>
                <div className="relative flex items-center gap-3 text-title-sm font-semibold">
                    <span className="grid h-11 w-11 place-items-center rounded-md bg-text-on-color/15 font-hanzi-reading text-[26px] font-bold">學</span>
                    {t(I18nKeys.common.appName)}
                </div>
                <div className="relative">
                    <h2 className="mb-4 max-w-[16ch] text-display-sm leading-[1.12] text-text-on-color">{t(I18nKeys.authBrand.heading)}</h2>
                    <p className="max-w-[40ch] text-body-lg opacity-85">
                        {t(I18nKeys.authBrand.body)}
                    </p>
                    <ul className="mt-8 flex list-none flex-col gap-4 p-0">
                        <BrandPoint bold={t(I18nKeys.authBrand.point1Bold)} text={t(I18nKeys.authBrand.point1Text)}/>
                        <BrandPoint bold={t(I18nKeys.authBrand.point2Bold)} text={t(I18nKeys.authBrand.point2Text)}/>
                        <BrandPoint bold={t(I18nKeys.authBrand.point3Bold)} text={t(I18nKeys.authBrand.point3Text)}/>
                    </ul>
                </div>
                <p className="relative text-caption opacity-70">{t(I18nKeys.authBrand.footer)}</p>
            </section>
            <section className="flex items-center justify-center bg-canvas px-5 pb-12 pt-20 lg:px-8">
                <div className="w-full max-w-[440px]">{children}</div>
            </section>
        </div>
    );
};

export interface AuthHeadProps {
    title: string;
    sub: string;
    badge?: 'glyph' | 'doc';
    sentTo?: string;
    signedInAs?: string;
}

export const AuthHead: React.FunctionComponent<AuthHeadProps> = ({title, sub, badge = 'glyph', sentTo, signedInAs}: AuthHeadProps) => {
    const t = useT();
    return (
        <div className="mb-6 flex flex-col items-start">
            {badge === 'glyph' ? (
                <span className="mb-5 grid h-14 w-14 place-items-center rounded-xl bg-brand font-hanzi-reading text-headline font-bold text-text-on-color" aria-hidden="true">學</span>
        ) : (
            <span className="mb-5 grid h-14 w-14 place-items-center rounded-xl bg-brand/15 text-brand-emphasis" aria-hidden="true">
                <FileText size={28}/>
            </span>
        )}
            <h1 className="mb-2 text-headline">{title}</h1>
            <p className="max-w-[40ch] text-body text-text-secondary">{sub}</p>
            {sentTo && (
            <span className="mt-4 inline-flex items-center gap-2 rounded-pill border border-border-subtle bg-surface-muted px-3.5 py-[7px] text-label font-semibold text-text-primary">
                <Mail size={16} className="text-text-muted"/>
                {sentTo}
            </span>
        )}
            {signedInAs && (
            <span className="mt-4 inline-flex items-center gap-2 rounded-pill border border-border-subtle bg-surface-muted px-3.5 py-[7px] text-label font-semibold text-text-primary">
                <User size={16} className="text-text-muted"/>
                {t(I18nKeys.common.signedInAs)} {signedInAs}
            </span>
        )}
        </div>
    );
};

export const AuthSwitchRow: React.FunctionComponent<React.PropsWithChildren> = ({children}: React.PropsWithChildren) => (
    <div className="mt-6 border-t border-border-subtle pt-5 text-center text-label text-text-secondary">
        {children}
    </div>
);
